import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import styled, { createGlobalStyle } from "styled-components";
import { LockOutlined, CloseOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import { campaignBriefAPI, type CampaignBriefResponse } from "@/api/campaignBrief";
import { DEFAULT_CAMPAIGN_DONTS, DEFAULT_CAMPAIGN_DOS, withDefaults } from "../../utils/briefDefaults";
import { LoadingWrapper } from "@/shared/components";
import { antdModalPhoneSheet } from "@/shared/components/Modal/phoneSheet";
import { MOBILE_GUTTER, TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

// ---------------------------------------------------------------------------
// Read-only creator-facing preview of a campaign brief. This is a MOCK of the
// next-phase creator view (the editable tab in ManageCampaign is unchanged).
// Data is passed in via `data`, defaulting to a Spider-Man sample so the mock
// renders standalone. Clicking a left-panel item scrolls the center content
// to that section.
// ---------------------------------------------------------------------------

export interface BriefGuideline {
	title: string;
	body: string;
	links: string[];
}

export interface CampaignBriefPreviewData {
	brandLogoText: string; // e.g. "SONY"
	title: string; // e.g. "Brand New Day — Campaign Brief"
	brand: string; // e.g. "Sony Pictures"
	region: string; // e.g. "Australia"
	updatedAt: string; // e.g. "Jun 17, 2026"
	releaseLabel: string; // e.g. "Release"
	releaseValue: string; // e.g. "Spider-Man: Brand New Day · in cinemas July 23"
	goLiveLabel: string; // e.g. "Go live"
	goLiveValue: string; // e.g. "Jul 12, 2026"
	about: string;
	objective: string;
	keyMessages: string;
	creativeDirection: string;
	videoFormatRequired: string;
	videoFormatSuggested: string[];
	dos: string[];
	donts: string[];
	guidelines: BriefGuideline[];
	timeline: { conceptDue: string; videoAssetsDue: string; goLive: string };
	callToAction: string;
	hashtags: string[];
}

// Mock defaults — the Spider-Man: Brand New Day sample from the design.
export const MOCK_CAMPAIGN_BRIEF: CampaignBriefPreviewData = {
	brandLogoText: "SONY",
	title: "Brand New Day — Campaign Brief",
	brand: "Sony Pictures",
	region: "Australia",
	updatedAt: "Jun 17, 2026",
	releaseLabel: "Release",
	releaseValue: "Spider-Man: Brand New Day · in cinemas July 23",
	goLiveLabel: "Go live",
	goLiveValue: "Jul 12, 2026",
	about: "The Mission: Every Hero Needs a Fresh Start. After the events that upended everything he thought he knew, Peter Parker starts over — no allies, no safety net, just the mask and the city. Spider-Man: Brand New Day is Sony Pictures' next chapter for the world's most beloved wall-crawler, in cinemas exclusively this July.",
	objective:
		'Drive advance ticket intent and opening-weekend turnout by making "Brand New Day" feel like an unmissable reset for the franchise. Your goal is to get your audience excited enough to book tickets before the reviews even land — by connecting the film\'s "starting over" theme to something real in your own life.',
	keyMessages:
		'1. "A hero with nothing left to lose": this isn\'t the Spider-Man you think you know — the mask is the only thing he has left.\n2. "Same web, new rules": fresh stakes, fresh threats, and a New York that doesn\'t trust him anymore.\n3. "In cinemas July 23": tickets are moving fast — don\'t wait to be over before you see it.',
	creativeDirection:
		"Your hook must land in the first 1–2 seconds, as either the first line you say or the first visual you show.",
	videoFormatRequired: "Official trailer or poster asset must appear within the first 15 seconds of the video.",
	videoFormatSuggested: [
		"Reaction / trailer breakdown — frame-by-frame, easter eggs, first impressions",
		'Day-in-the-life "reset" story tied back to the film\'s theme',
		"Ranking / tier list — suits, villains, or eras — landing on the new film",
	],
	dos: [
		"Use only the approved trailer/poster assets from your creator kit",
		"Include the official release date and ticketing link in your caption",
		'Say "Spider-Man: Brand New Day" in full at least once, on screen or in the hook',
		"Keep it spoiler-free beyond officially released marketing",
	],
	donts: [
		"Share or react to leaked or unofficial footage",
		"Speculate on plot details not present in official marketing",
		"Compare unfavourably to other studios' superhero films",
		"Reveal ticketing discount codes before the embargo lifts",
	],
	guidelines: [
		{
			title: "Music Guidelines",
			body: "TikTok / Meta commercial library only, unless using the officially licensed campaign audio provided in your creator kit.",
			links: ["https://ads.tiktok.com/business/creativecenter/music/pc/en", "https://www.facebook.com/sound"],
		},
		{
			title: "Safe zones",
			body: "Keep the film title and release date legible in platform safe zones — don't cover them with captions.",
			links: ["https://ads.tiktok.com/toolbox/preview", "https://www.facebook.com/business/help/103816146375741"],
		},
		{
			title: "Sending for approval",
			body: "Don't export from the TikTok editor (adds a watermark). Submit hi-res via WeTransfer, Google Drive or Dropbox at least 5 business days before Go Live for studio approval.",
			links: [],
		},
	],
	timeline: { conceptDue: "Jun 27, 2026", videoAssetsDue: "Jul 04, 2026", goLive: "Jul 12, 2026" },
	callToAction: '"Spider-Man: Brand New Day — only in cinemas July 23. Get your tickets now."',
	hashtags: ["@spidermanmovie", "#SpiderManBrandNewDay", "#BrandNewDay", "#SpiderMan", "#ad"],
};

// Minimal campaign metadata used for the modal header (name / brand / region /
// updated date). Sourced from the campaign record, not the brief.
export interface CampaignBriefMeta {
	name?: string;
	brand?: string[];
	client?: string[];
	location?: string[];
	lastModificationTime?: string;
}

const fmtDate = (d: string | null | undefined): string => (d ? dayjs(d).format("MMM DD, YYYY") : "—");

// Maps the brief API response (+ campaign meta) to the read-only display shape.
// Fields with no backing column (Release line, region, logo) are derived from
// the campaign record or left blank.
export function briefResponseToPreview(
	brief: CampaignBriefResponse,
	meta?: CampaignBriefMeta,
): CampaignBriefPreviewData {
	const brand = meta?.brand?.[0] || meta?.client?.[0] || "";
	return {
		brandLogoText:
			(brand || "BRAND")
				.replace(/[^A-Za-z0-9]/g, "")
				.slice(0, 4)
				.toUpperCase() || "BRAND",
		title: `${meta?.name || "Campaign"} — Campaign Brief`,
		brand: brand || "—",
		region: meta?.location?.[0] || "—",
		updatedAt: fmtDate(brief.lastModifiedAt || meta?.lastModificationTime),
		releaseLabel: "Release",
		releaseValue: meta?.name || "—",
		goLiveLabel: "Go live",
		goLiveValue: fmtDate(brief.goLiveDate),
		about: brief.about || "—",
		objective: brief.objective || "—",
		keyMessages: brief.keyMessages || "—",
		creativeDirection: brief.creativeDirection || "—",
		videoFormatRequired: brief.videoFormatRequiredElement || "—",
		videoFormatSuggested: (brief.videoFormatSuggestedFormats || "")
			.split("\n")
			.map((s) => s.replace(/^[-•\s]+/, "").trim())
			.filter(Boolean),
		// Same normalization as the editable tab (useCampaignBrief) — every brief
		// must always show the standard Do's/Don'ts, even if it was never saved
		// through the editable tab yet.
		dos: withDefaults(brief.campaignDos || [], DEFAULT_CAMPAIGN_DOS),
		donts: withDefaults(brief.campaignDonts || [], DEFAULT_CAMPAIGN_DONTS),
		guidelines: brief.standardGuidelines || [],
		timeline: {
			conceptDue: fmtDate(brief.conceptDueDate),
			videoAssetsDue: fmtDate(brief.videoAssetsDueDate),
			goLive: fmtDate(brief.goLiveDate),
		},
		callToAction: brief.callToAction || "—",
		hashtags: brief.hashtags || [],
	};
}

const SECTIONS = [
	{ id: "campaign-details", label: "Campaign Details" },
	{ id: "about", label: "About the Campaign" },
	{ id: "objective", label: "Campaign Objective" },
	{ id: "key-messages", label: "Key Messages" },
	{ id: "creative-direction", label: "Creative Direction" },
	{ id: "video-format", label: "Video Format" },
	{ id: "dos-donts", label: "Do's & Don'ts" },
	{ id: "brand-guidelines", label: "Brand Guidelines" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

const PURPLE = "#3A0CA3";
const HEADING: React.CSSProperties = {
	fontSize: 13,
	fontWeight: 700,
	letterSpacing: 0.6,
	color: "#111827",
	textTransform: "uppercase",
	marginBottom: 12,
};
const BODY_TEXT: React.CSSProperties = { fontSize: 15, lineHeight: 1.6, color: "#374151" };
const SIDE_LABEL: React.CSSProperties = {
	fontSize: 12,
	fontWeight: 700,
	letterSpacing: 0.6,
	color: "#9CA3AF",
	textTransform: "uppercase",
	marginBottom: 10,
};

/* ─── Layout. Desktop: three columns (section nav · content · facts). Phones: a 60px-gap sheet with
   one scroller; the section nav becomes a pinned chip rail and the facts move into the flow. ─── */

/*
 * The brief opens from the creator's Campaign Info sheet, which sits above the phone bottom bar at
 * z-index 2100, so on phones the modal has to sit above that sheet. Desktop stacking is unchanged.
 */
const PhoneStacking = createGlobalStyle`
	${mediaPhone`
		.campaign-brief-preview-root .ant-modal-mask,
		.campaign-brief-preview-root .ant-modal-wrap {
			z-index: 2200 !important;
		}
	`}
`;

const Shell = styled(Modal)`
	${antdModalPhoneSheet}

	${mediaPhone`
		.ant-modal-body {
			display: flex;
			flex: 1;
			flex-direction: column;
			min-height: 0;
		}
	`}
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 20px 24px;
	border-bottom: 1px solid #eef0f4;

	${mediaPhone`
		flex-shrink: 0;
		padding: 16px 8px 12px ${MOBILE_GUTTER}px;
	`}
`;

const HeaderIdentity = styled.div`
	display: flex;
	align-items: center;
	gap: 14px;
	min-width: 0;

	${mediaPhone`gap: 12px;`}
`;

const LogoBadge = styled.div`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background: ${PURPLE};
	color: #fff;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.5px;

	${mediaPhone`
		width: 40px;
		height: 40px;
		border-radius: 10px;
		font-size: 11px;
	`}
`;

const HeaderTitle = styled.div`
	color: ${PURPLE};
	font-size: 22px;
	font-weight: 700;

	${mediaPhone`
		display: -webkit-box;
		overflow: hidden;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		font-size: 18px;
		line-height: 1.25;
	`}
`;

const HeaderMeta = styled.div`
	margin-top: 2px;
	color: #8b8b99;
	font-size: 14px;

	${mediaPhone`
		overflow: hidden;
		font-size: 12px;
		white-space: nowrap;
		text-overflow: ellipsis;
	`}
`;

const HeaderActions = styled.div`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	gap: 12px;

	${mediaPhone`gap: 4px;`}
`;

const ReadOnlyBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	border-radius: 16px;
	background: #eef0fe;
	color: #4338ca;
	font-size: 13px;
	font-weight: 500;

	/* Phones keep the lock as the signal and drop the label. */
	${mediaPhone`
		padding: 8px;

		.read-only-label {
			display: none;
		}
	`}
`;

const CloseButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	border: none;
	border-radius: 8px;
	background: #f3f4f6;
	color: #4b5563;
	font-size: 16px;
	cursor: pointer;

	${mediaPhone`
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
		background: transparent;
	`}

	${pressedTint}
`;

const BodyGrid = styled.div`
	display: grid;
	grid-template-columns: 220px 1fr 280px;
	height: 68vh;

	${mediaPhone`
		position: relative;
		display: flex;
		flex: 1;
		flex-direction: column;
		height: auto;
		min-height: 0;
		overflow-y: auto;
		overscroll-behavior: contain;
	`}
`;

const SectionNav = styled.nav`
	padding: 24px 16px;
	border-right: 1px solid #f1f2f6;
	overflow-y: auto;

	${mediaPhone`
		position: sticky;
		top: 0;
		z-index: 2;
		display: flex;
		flex-shrink: 0;
		gap: 8px;
		padding: 12px ${MOBILE_GUTTER}px;
		border-right: 0;
		border-bottom: 1px solid #f1f2f6;
		background: #fff;
		overflow-x: auto;
		overflow-y: hidden;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		.section-nav-label {
			display: none;
		}
	`}
`;

const SectionLink = styled.button<{ $active: boolean }>`
	display: block;
	width: 100%;
	margin-bottom: 2px;
	padding: 10px 12px;
	border: none;
	border-radius: 8px;
	background: ${({ $active }) => ($active ? "#EEF0FE" : "transparent")};
	color: ${({ $active }) => ($active ? PURPLE : "#6B7280")};
	font-size: 15px;
	font-weight: ${({ $active }) => ($active ? 600 : 500)};
	text-align: left;
	cursor: pointer;

	${({ $active }) => mediaPhone`
		flex-shrink: 0;
		width: auto;
		min-height: 40px;
		margin: 0;
		padding: 0 14px;
		border-radius: 999px;
		background: ${$active ? "#EEF0FE" : "#F6F7F9"};
		font-size: 14px;
		white-space: nowrap;
	`}

	${pressedTint}
`;

const Content = styled.div`
	position: relative;
	padding: 24px 28px;
	overflow-y: auto;

	${mediaPhone`
		order: 2;
		padding: 16px ${MOBILE_GUTTER}px 24px;
		overflow: visible;
	`}
`;

const TwoColumns = styled.div<{ $gap: number }>`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${({ $gap }) => $gap}px;

	${mediaPhone`
		grid-template-columns: minmax(0, 1fr);
		gap: 12px;
	`}
`;

const DetailsCard = styled(TwoColumns)`
	padding: 18px 22px;
	border: 1px solid #ececf1;
	border-radius: 12px;

	${mediaPhone`padding: 16px;`}
`;

const Facts = styled.aside`
	padding: 24px 22px;
	border-left: 1px solid #f1f2f6;
	background: #fcfcfd;
	overflow-y: auto;

	${mediaPhone`
		order: 1;
		padding: 16px ${MOBILE_GUTTER}px;
		border-left: 0;
		border-bottom: 1px solid #f1f2f6;
		overflow: visible;
	`}
`;

/* Phones lay the three timeline dates side by side so the facts stay compact above the brief. */
const TimelineFacts = styled.div`
	${mediaPhone`
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		margin-bottom: 4px;

		> div {
			margin-bottom: 12px !important;
		}
	`}
`;

interface Props {
	open: boolean;
	onClose: () => void;
	// Explicit display data (overrides everything). If omitted and `campaignId`
	// is given, the brief is fetched and mapped. If both omitted, the mock is used.
	data?: CampaignBriefPreviewData;
	// Load this campaign's real brief via the API.
	campaignId?: string | null;
	// Campaign record used for the header (name / brand / region / updated).
	campaign?: CampaignBriefMeta | null;
}

export const CampaignBriefPreviewModal: React.FC<Props> = ({ open, onClose, data, campaignId, campaign }) => {
	const [active, setActive] = useState<SectionId>("campaign-details");
	const [loaded, setLoaded] = useState<CampaignBriefPreviewData | null>(null);
	const [loading, setLoading] = useState(false);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const bodyRef = useRef<HTMLDivElement | null>(null);
	const navRef = useRef<HTMLElement | null>(null);
	const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
	// Ignore scroll-spy briefly right after a click so the highlight doesn't
	// flicker to intermediate sections while smooth-scrolling.
	const clickScrollUntil = useRef(0);

	// Resolve what to render: explicit data > fetched data > mock (only when no
	// campaignId was supplied, i.e. pure design preview).
	const display: CampaignBriefPreviewData | null = data ?? loaded ?? (campaignId ? null : MOCK_CAMPAIGN_BRIEF);

	// Fetch the real brief when opened with a campaignId and no explicit data.
	useEffect(() => {
		if (!open || data || !campaignId) return;
		let cancelled = false;
		setLoading(true);
		setLoaded(null);
		campaignBriefAPI
			.get(campaignId)
			.then((res) => {
				if (!cancelled) setLoaded(briefResponseToPreview(res, campaign ?? undefined));
			})
			.catch(() => {
				if (!cancelled) setLoaded(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, data, campaignId, campaign]);

	// The centre column scrolls on desktop; on phones the whole body does (with the section nav pinned
	// at its top). Pick whichever is actually scrollable, and measure sections against it.
	const getScroller = useCallback(() => {
		const content = contentRef.current;
		if (content && getComputedStyle(content).overflowY !== "visible") return { container: content, inset: 8 };
		return { container: bodyRef.current, inset: (navRef.current?.offsetHeight ?? 0) + 8 };
	}, []);

	const sectionOffset = (el: HTMLElement, container: HTMLElement) =>
		el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;

	const scrollTo = useCallback(
		(id: SectionId) => {
			setActive(id);
			clickScrollUntil.current = Date.now() + 700;
			const el = sectionRefs.current[id];
			const { container, inset } = getScroller();
			if (el && container) {
				container.scrollTo({ top: sectionOffset(el, container) - inset, behavior: "smooth" });
			}
		},
		[getScroller],
	);

	// Scroll-spy: highlight the section nearest the top of the scroll area. Listens on both possible
	// scrollers; only the one that actually scrolls fires.
	useEffect(() => {
		if (!open) return;
		const targets = [contentRef.current, bodyRef.current].filter(Boolean) as HTMLElement[];
		const onScroll = () => {
			if (Date.now() < clickScrollUntil.current) return;
			const { container, inset } = getScroller();
			if (!container) return;
			const top = container.scrollTop + inset + 16;
			let current: SectionId = SECTIONS[0].id;
			for (const s of SECTIONS) {
				const el = sectionRefs.current[s.id];
				if (el && sectionOffset(el, container) <= top) current = s.id;
			}
			setActive(current);
		};
		targets.forEach((target) => target.addEventListener("scroll", onScroll, { passive: true }));
		return () => targets.forEach((target) => target.removeEventListener("scroll", onScroll));
	}, [open, getScroller, loaded]);

	// Keep the active chip visible in the phone's horizontal section rail.
	// Scrolls the rail itself rather than calling scrollIntoView: that would also scroll the body, and
	// it cancels the smooth scroll a chip tap has just started.
	useEffect(() => {
		const nav = navRef.current;
		const chip = nav?.querySelector<HTMLElement>('[aria-current="true"]');
		if (!nav || !chip || nav.scrollWidth <= nav.clientWidth) return;
		const left = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2;
		nav.scrollTo({ left, behavior: "smooth" });
	}, [active]);

	// Reset to the top section each time the modal opens.
	useEffect(() => {
		if (open) {
			setActive("campaign-details");
			requestAnimationFrame(() => {
				contentRef.current?.scrollTo({ top: 0 });
				bodyRef.current?.scrollTo({ top: 0 });
			});
		}
	}, [open]);

	const setSectionRef = (id: string) => (el: HTMLDivElement | null) => {
		sectionRefs.current[id] = el;
	};

	return (
		<Shell
			rootClassName="campaign-brief-preview-root"
			open={open}
			onCancel={onClose}
			footer={null}
			closable={false}
			centered
			width={1160}
			styles={{
				content: { padding: 0, borderRadius: 20, overflow: "hidden" },
				body: { padding: 0 },
			}}
		>
			<PhoneStacking />
			{!display ? (
				<LoadingWrapper isLoading={loading} label="Loading brief">
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: 320,
							color: "#8b8b99",
						}}
					>
						{!loading && "No brief available"}
					</div>
				</LoadingWrapper>
			) : (
				<>
					<Header>
						<HeaderIdentity>
							<LogoBadge>{display.brandLogoText}</LogoBadge>
							<div style={{ minWidth: 0 }}>
								<HeaderTitle>{display.title}</HeaderTitle>
								<HeaderMeta>
									{display.brand} · {display.region} · Updated {display.updatedAt}
								</HeaderMeta>
							</div>
						</HeaderIdentity>
						<HeaderActions>
							<ReadOnlyBadge aria-label="Read-only">
								<LockOutlined /> <span className="read-only-label">Read-only</span>
							</ReadOnlyBadge>
							<CloseButton type="button" aria-label="Close" onClick={onClose}>
								<CloseOutlined />
							</CloseButton>
						</HeaderActions>
					</Header>

					<BodyGrid ref={bodyRef}>
						<SectionNav ref={navRef} aria-label="Brief sections">
							<div style={SIDE_LABEL} className="section-nav-label">
								On this page
							</div>
							{SECTIONS.map((s) => (
								<SectionLink
									key={s.id}
									type="button"
									$active={active === s.id}
									aria-current={active === s.id ? "true" : undefined}
									onClick={() => scrollTo(s.id)}
								>
									{s.label}
								</SectionLink>
							))}
						</SectionNav>

						{/* Center content (scrollable) */}
						<Content ref={contentRef}>
							{/* Campaign Details card */}
							<div ref={setSectionRef("campaign-details")}>
								<DetailsCard $gap={24}>
									<div>
										<div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 6 }}>
											{display.releaseLabel}
										</div>
										<div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
											{display.releaseValue}
										</div>
									</div>
									<div>
										<div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 6 }}>
											{display.goLiveLabel}
										</div>
										<div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
											{display.goLiveValue}
										</div>
									</div>
								</DetailsCard>
							</div>

							<div ref={setSectionRef("about")} style={{ marginTop: 32 }}>
								<div style={HEADING}>About the Campaign</div>
								<div style={BODY_TEXT}>{display.about}</div>
							</div>

							<div ref={setSectionRef("objective")} style={{ marginTop: 32 }}>
								<div style={HEADING}>Campaign Objective</div>
								<div style={BODY_TEXT}>{display.objective}</div>
							</div>

							<div ref={setSectionRef("key-messages")} style={{ marginTop: 32 }}>
								<div style={HEADING}>Key Messages</div>
								<div style={{ ...BODY_TEXT, whiteSpace: "pre-line" }}>{display.keyMessages}</div>
							</div>

							<div ref={setSectionRef("creative-direction")} style={{ marginTop: 32 }}>
								<div style={HEADING}>Creative Direction</div>
								<div style={BODY_TEXT}>{display.creativeDirection}</div>
							</div>

							<div ref={setSectionRef("video-format")} style={{ marginTop: 32 }}>
								<div style={HEADING}>Video Format</div>
								<div style={{ ...BODY_TEXT, fontWeight: 600, marginBottom: 10 }}>
									{display.videoFormatRequired}
								</div>
								<ul style={{ ...BODY_TEXT, paddingLeft: 20, margin: 0 }}>
									{display.videoFormatSuggested.map((s, i) => (
										<li key={i} style={{ marginBottom: 6 }}>
											{s}
										</li>
									))}
								</ul>
							</div>

							<div ref={setSectionRef("dos-donts")} style={{ marginTop: 32 }}>
								<div style={HEADING}>Do's &amp; Don'ts</div>
								<TwoColumns $gap={16}>
									<div style={{ background: "#F6F7F9", borderRadius: 12, padding: 16 }}>
										<div style={{ fontWeight: 700, color: "#1E7A34", marginBottom: 10 }}>Do</div>
										{display.dos.map((d, i) => (
											<div
												key={i}
												style={{
													display: "flex",
													alignItems: "flex-start",
													gap: 8,
													marginBottom: 10,
												}}
											>
												<CheckCircleFilled style={{ color: "#22A34A", marginTop: 3 }} />
												<span style={{ fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
													{d}
												</span>
											</div>
										))}
									</div>
									<div style={{ background: "#F6F7F9", borderRadius: 12, padding: 16 }}>
										<div style={{ fontWeight: 700, color: "#D30024", marginBottom: 10 }}>Don't</div>
										{display.donts.map((d, i) => (
											<div
												key={i}
												style={{
													display: "flex",
													alignItems: "flex-start",
													gap: 8,
													marginBottom: 10,
												}}
											>
												<CloseCircleFilled style={{ color: "#E23744", marginTop: 3 }} />
												<span style={{ fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
													{d}
												</span>
											</div>
										))}
									</div>
								</TwoColumns>
							</div>

							<div ref={setSectionRef("brand-guidelines")} style={{ marginTop: 32, paddingBottom: 8 }}>
								<div style={HEADING}>Brand Guidelines</div>
								<div style={{ background: "#F6F7F9", borderRadius: 12, padding: "8px 20px" }}>
									{display.guidelines.map((g, i) => (
										<div
											key={i}
											style={{
												padding: "16px 0",
												borderTop: i === 0 ? "none" : "1px solid #E9EAEE",
											}}
										>
											<div style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>
												{g.title}
											</div>
											<div style={{ fontSize: 14, color: "#374151", lineHeight: 1.55 }}>
												{g.body}
											</div>
											{g.links.map((link) => (
												<a
													key={link}
													href={link}
													target="_blank"
													rel="noreferrer"
													style={{
														display: "block",
														color: PURPLE,
														fontSize: 14,
														marginTop: 4,
														wordBreak: "break-all",
													}}
												>
													{link}
												</a>
											))}
										</div>
									))}
								</div>
							</div>
						</Content>

						<Facts>
							<div style={SIDE_LABEL}>Timeline</div>
							<TimelineFacts>
								{[
									["Concept due", display.timeline.conceptDue],
									["Video assets due", display.timeline.videoAssetsDue],
									["Go live", display.timeline.goLive],
								].map(([label, value]) => (
									<div key={label} style={{ marginBottom: 16 }}>
										<div style={{ fontSize: 13, color: "#9CA3AF" }}>{label}</div>
										<div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{value}</div>
									</div>
								))}
							</TimelineFacts>

							<div style={{ ...SIDE_LABEL, marginTop: 20 }}>Call to Action</div>
							<div
								style={{
									fontSize: 15,
									fontWeight: 600,
									color: "#111827",
									lineHeight: 1.5,
									marginBottom: 20,
								}}
							>
								{display.callToAction}
							</div>

							<div style={SIDE_LABEL}>Tag &amp; Hashtags</div>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
								{display.hashtags.map((t) => (
									<span
										key={t}
										style={{
											background: "#EEF0FE",
											color: "#4338CA",
											borderRadius: 8,
											padding: "6px 12px",
											fontSize: 14,
										}}
									>
										{t}
									</span>
								))}
							</div>
						</Facts>
					</BodyGrid>
				</>
			)}
		</Shell>
	);
};
